import React, { useState, useEffect, useCallback } from 'react';

let logoutTimer;

const nullContextData = {
  token: '',
  isLoggedIn: false,
  userId: undefined,
  userFullName: "",
  userRole: undefined
}

const AuthContext = React.createContext({
  ...nullContextData,
  login: (token, expirationTime, user_id, user_full_name, user_role) => {},
  logout: () => {},
});

const calculateRemainingTime = (expirationTime) => {
  const currentTime = new Date().getTime();
  const adjExpirationTime = new Date(Number(expirationTime)).getTime();
  return adjExpirationTime - currentTime;  // remaining time in ms
};

const tokenKey = 'auth-token-key'
const expirationTimeKey = 'auth-expiration-time'
const userIdKey = 'auth-user-id'
const userFullNameKey = 'auth-user-full-name'
const userRoleKey = 'auth-user-role-key'

const writeToSessionStorage = (token, expirationTime, user_id, user_full_name, user_role) => {
  sessionStorage.setItem(tokenKey, token);
  sessionStorage.setItem(expirationTimeKey, expirationTime);
  sessionStorage.setItem(userIdKey, user_id);
  sessionStorage.setItem(userFullNameKey, user_full_name);
  sessionStorage.setItem(userRoleKey, user_role);
}

const clearSessionStorage = () => {
  sessionStorage.removeItem(tokenKey);
  sessionStorage.removeItem(expirationTimeKey);
  sessionStorage.removeItem(userIdKey);
  sessionStorage.removeItem(userFullNameKey);
  sessionStorage.removeItem(userRoleKey);
}

const retrieveStoredUserData = () => {
  const storedToken = sessionStorage.getItem(tokenKey);
  const storedExpirationDate = sessionStorage.getItem(expirationTimeKey);
  const storedUserId = sessionStorage.getItem(userIdKey);
  const storedUserFullName = sessionStorage.getItem(userFullNameKey);
  const storedUserRole = sessionStorage.getItem(userRoleKey);

  const remainingTime = calculateRemainingTime(storedExpirationDate);  // In ms

  const minRemainingTime = 60000  // ms: if storedDataValidityTime is less than that, we log out
  if (remainingTime <= minRemainingTime || !storedToken || !storedUserId || !storedUserFullName || !storedUserRole) {
    clearSessionStorage();
    return [null, null];
  } else {
    return [remainingTime, {
      token: storedToken,
      isLoggedIn: true,
      userId: storedUserId,
      userFullName: storedUserFullName,
      userRole: storedUserRole
    }];
  }
};

export const adminRoleConstant = 'admin';
export const nonAdminRoleConstant = 'undefined_role';
export const isAdminRole = (role_string) => (role_string === 'admin')

export const AuthContextProvider = (props) => {
  const [storedDataRemainingTime, storedData] = retrieveStoredUserData();
  let initialUserData = (storedDataRemainingTime) ? storedData : nullContextData;
  const [userData, setUserData] = useState(initialUserData);

  const logoutHandler = useCallback(() => {
    setUserData(nullContextData);
    clearSessionStorage();

    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }
  }, []);

  const loginHandler = (token, expirationTime, user_id, user_full_name, user_role) => {
    const newUserData = {token: token, isLoggedIn: true,
      userId: user_id, userFullName: user_full_name, userRole: user_role}
    setUserData(newUserData);

    writeToSessionStorage(token, expirationTime, user_id, user_full_name, user_role);

    // Setup auto log out at expiration time
    const remainingTime = calculateRemainingTime(expirationTime);
    logoutTimer = setTimeout(logoutHandler, remainingTime);
  };

  useEffect(() => {
    if (storedDataRemainingTime >= 0) {
      logoutTimer = setTimeout(logoutHandler, storedDataRemainingTime);
    }
  }, [storedData, storedDataRemainingTime, logoutHandler]);

  const contextValue = {
    token: userData.token,
    isLoggedIn: userData.isLoggedIn,
    userId: userData.userId,
    userFullName: userData.userFullName,
    userRole: userData.userRole,
    login: loginHandler,
    logout: logoutHandler,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
